import {
  BrowserMessageReceiveType,
  BrowserMessageType,
  exchangeElectronMessage as exchangeBrowserElectron,
  exchangeElectronMessage,
  registerElectronMessageReceiver,
  sendElectronMessage,
} from 'electron-communication-lib/BrowserForApp';
import {makeAutoObservable, runInAction} from 'mobx';

import handledBackend, {
  LockedDocumentInResponse,
} from '@lexian/api/documents-api';
import {isElectron} from '_/utilities';

import {logout} from '../utils/logout';

export type DialogType =
  | 'IN_AN_ACTION'
  | 'LAST_LOGIN'
  | 'FROM_MENU'
  | 'LOGGING_OUT'
  | 'LOGGING_OUT_UNREACHABLE'
  | 'CLOSING_APP'
  | 'CLOSING_APP_UNREACHABLE'
  | 'CLOSING_APP_UNAUTHORIZED'
  | 'UPDATING_APP'
  | 'UPDATING_APP_UNREACHABLE'
  | 'UPDATING_APP_UNAUTHORIZED'
  | 'NONE';

class UnauthorizedError extends Error {
  public constructor() {
    super('Unauthorized');
  }
}

class ServerUnreachableError extends Error {
  public constructor() {
    super('Server unreachable');
  }
}

export class FilesInUseStore {
  public dialogType: DialogType = 'NONE';

  public filesInUse: LockedDocumentInResponse[];

  public readonly eventTarget: EventTarget = new EventTarget();

  private loggedInUsername: string;

  public constructor(loggedInUsername?: string) {
    this.loggedInUsername = loggedInUsername ?? '';
    makeAutoObservable(this, {}, {autoBind: true});

    this.filesInUse = [];

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    window.addEventListener('message', this.openFileMessageHandler);
    window.addEventListener('message', this.userInfoMessageHandler);

    registerElectronMessageReceiver(
      BrowserMessageReceiveType.CLOSE_PREVENTED_BY_OPEN_FILES,
      () => {
        this.handleClosePreventedByOpenFiles();
      }
    );
  }

  public setLoggedInUsername(loggedInUsername: string): void {
    this.loggedInUsername = loggedInUsername;
  }

  public async handleClosePreventedByOpenFiles(): Promise<void> {
    try {
      const files = await getLockedDocuments(true);
      if (files.length > 0) {
        runInAction(() => {
          this.dialogType = 'CLOSING_APP';
          this.filesInUse = files;
        });
      } else {
        this.quitRegularly();
      }
    } catch (error) {
      runInAction(() => {
        if (error instanceof UnauthorizedError) {
          this.dialogType = 'CLOSING_APP_UNAUTHORIZED';
          this.filesInUse = [];
          return;
        }

        this.dialogType = 'CLOSING_APP_UNREACHABLE';
        this.filesInUse = [];
      });
    }
  }

  public tryClosingAgain(): Promise<void> {
    return this.handleClosePreventedByOpenFiles();
  }

  public async handleLogoutPreventedByOpenFiles(): Promise<void> {
    try {
      const files = await getLockedDocuments(true);
      if (files.length > 0) {
        runInAction(() => {
          this.dialogType = 'LOGGING_OUT';
          this.filesInUse = files;
        });
      } else {
        this.logout();
      }
    } catch {
      runInAction(() => {
        this.dialogType = 'LOGGING_OUT_UNREACHABLE';
        this.filesInUse = [];
      });
    }
  }

  public tryUpdatingAgain(): Promise<void> {
    return this.handleUpdatePreventedByOpenFiles();
  }

  public async handleUpdatePreventedByOpenFiles(): Promise<void> {
    try {
      const files = await getLockedDocuments(true);
      if (files.length > 0) {
        runInAction(() => {
          this.dialogType = 'UPDATING_APP';
          this.filesInUse = files;
        });
      } else {
        this.update();
      }
    } catch (error) {
      runInAction(() => {
        this.dialogType =
          error instanceof UnauthorizedError
            ? 'UPDATING_APP_UNAUTHORIZED'
            : 'UPDATING_APP_UNREACHABLE';
        this.filesInUse = [];
      });
    }
  }

  private async openFileMessageHandler(event: MessageEvent): Promise<void> {
    if (event.data?.type === 'renderer') {
      const {status, data} = event.data.payload;
      if (status === 'file-locked' && data) {
        const {documentId, casusId} = data;
        const document = await getLockedDocumentById(documentId, casusId);
        if (document) {
          runInAction(() => {
            this.dialogType = 'IN_AN_ACTION';
            this.filesInUse = [document];
          });
        }
      }
    }
  }

  // Triggered when a new user logs in
  private userInfoMessageHandler(event: MessageEvent): void {
    if (event.data?.type === BrowserMessageType.SEND_USER_INFO) {
      exchangeElectronMessage(
        {
          payload: null,
          type: BrowserMessageType.STALE_DOCUMENT_IDS_QUERY,
        },
        (staleDocumentIds) => {
          this.handleStaleDocumentIds(staleDocumentIds);
        }
      );
      window.removeEventListener('message', this.userInfoMessageHandler);
    }
  }

  private async handleStaleDocumentIds(
    staleDocumentIds: string[]
  ): Promise<void> {
    if (staleDocumentIds.length > 0) {
      const lockedDocuments = await getLockedDocuments();
      runInAction(() => {
        if (lockedDocuments.length > 0) {
          this.dialogType = 'LAST_LOGIN';
          this.filesInUse = lockedDocuments?.filter(({documentId}) =>
            staleDocumentIds.includes(documentId)
          );
        }
      });
    }
  }

  public async showFilesInUse(): Promise<void> {
    const lockedDocuments = await getLockedDocuments();
    runInAction(() => {
      this.dialogType = 'FROM_MENU';
      this.filesInUse = lockedDocuments;
    });
  }

  public async refreshLockedDocuments(): Promise<void> {
    const files = await getLockedDocuments();
    runInAction(() => {
      this.filesInUse = files;
    });
    if (!this.filesInUse) {
      this.clearAllFiles();
    }
  }

  public clearAllFiles(): void {
    runInAction(() => {
      this.dialogType = 'NONE';
      this.filesInUse = [];
    });
  }

  public get hasFilesInUse(): boolean {
    return this.filesInUse.length > 0;
  }

  public async releaseAllStaleDocuments(): Promise<void> {
    if (this.filesInUse) {
      await Promise.all(
        this.filesInUse.map(async (document) => {
          await releaseDocument(document);
        })
      );
      this.clearAllFiles();
    }
  }

  public async logout(): Promise<void> {
    this.clearAllFiles();
    await logout(this.loggedInUsername);
  }

  public quitAnyway(): void {
    this.clearAllFiles();
    sendElectronMessage({
      payload: null,
      type: BrowserMessageType.FORCE_EXIT_APPLICATION,
    });
  }

  public quitRegularly(): void {
    this.clearAllFiles();
    sendElectronMessage({
      payload: null,
      type: BrowserMessageType.SAFE_EXIT_APPLICATION,
    });
  }

  public update(): void {
    this.clearAllFiles();
    sendElectronMessage({
      payload: null,
      type: BrowserMessageType.UPDATE_APPLICATION,
    });
  }
}

export const releaseDocument = async (
  document: LockedDocumentInResponse
): Promise<void> => {
  await unlockDocument(document.documentId, document.casusId);
};

const unlockDocument = async (
  documentId: string,
  casusId: string
): Promise<boolean | undefined> => {
  const response = await handledBackend.unlockDocument({
    parameters: {
      documentId,
      casusId,
    },
  });
  if (!response.hasError) {
    return true;
  }
};

export const getLockedDocuments = (
  propagateError?: boolean
): Promise<LockedDocumentInResponse[]> =>
  new Promise((resolve, reject) =>
    handledBackend.getLockedDocumentsOfLoggedInUser({
      parameters: {},
      onSuccess: ({body}) => {
        if (!isElectron()) {
          resolve(body.lockedDocuments);
        } else {
          exchangeBrowserElectron(
            {
              type: BrowserMessageType.GET_WATCHED_DOCUMENT_IDS,
              payload: null,
            },
            (lockedDocumentIds) => {
              const lockedDocumentsOnCurrentWorkstation =
                body.lockedDocuments.filter((lockedDocument) =>
                  lockedDocumentIds.includes(lockedDocument.documentId)
                );
              resolve(lockedDocumentsOnCurrentWorkstation);
            }
          );
        }
      },
      localHandlers: {
        '401': () => {
          if (propagateError) {
            reject(new UnauthorizedError());
          }
        },
      },
      afterErrorHandler: () => {
        if (propagateError) {
          reject(new ServerUnreachableError());
        }
      },
    })
  );

const getLockedDocumentById = async (
  documentId: string,
  casusId: string
): Promise<LockedDocumentInResponse | undefined> => {
  const response = await handledBackend.getLockedDocumentById({
    parameters: {
      casusId,
      documentId,
    },
  });
  if (!response.hasError) {
    return response.body?.body.document;
  }
};
